import {
  WebChatAgentReadReceiptEvent,
  WebChatAgentTypingIndicatorEvent,
  WebChatParticipant,
} from '@ucc/cc-common-ui';
import { TYPING_INDICATOR_SET_TO_FALSE_TIME_OUT } from '@ucc/cc-common-ui/utils/hooks';
import { useEffect } from 'react';

import { fetchAgent } from '../state/Agents/actions';
import {
  addEvent,
  agentTypingIndicator,
  mergeMessageEvent,
  setEvents,
} from '../state/Conversation';
import { setError } from '../state/Error';
import { ErrorType } from '../state/Error/types';
import { setInitialState, updateInitialState } from '../state/Notifications';
import { subscribe } from '../state/Notifications/actions';
import {
  toAfterHoursEvent,
  toChatEndedEvent,
  toMessageEvent,
  toSmartResponseSelectQuestion,
  toWebChatSmartResponseSelectQuestionEvent,
  toWebChatSurveyFeedbackQuestionEvent,
  toWebChatSurveyFeedbackResponseEvent,
  toWebChatSurveyInitializedEvent,
  toWebChatSurveyRatingResponseEvent,
} from '../state/Notifications/helpers';
import {
  NotificationEventsType,
  WebChatAfterHoursMessageEvent,
  WebChatEnded,
  WebChatInboundMessage,
  WebChatInitialState,
  WebChatInitialStateContent,
  WebChatMessage,
  WebChatOutboundMessage,
  WebChatSmartResponseSelectQuestion,
  WebChatSurveyFeedbackQuestion,
  WebChatSurveyFeedbackResponse,
  WebChatSurveyInitialized,
  WebChatSurveyRatingResponse,
} from '../state/Notifications/types';
import { AppDispatch } from '../state/reducer';
import { WidgetConfiguration } from '../state/Widget/types';
import { useAppDispatch } from '../utils/hooks';
import { dbDelete, dbGet, dbPut } from '../utils/storage';
import {
  AgentDB,
  ChannelDB,
  ConversationDB,
  ParticipantDB,
  WidgetDB,
} from '../utils/storage/schemas';

let setTypingIndicatorToFalseTimeOut;
const onAgentTypingIndicatorEvent = (
  dispatch: AppDispatch,
  event: WebChatAgentTypingIndicatorEvent
) => {
  clearTimeout(setTypingIndicatorToFalseTimeOut);

  dispatch(agentTypingIndicator.actions.setTypingIndicator(event));

  if (event.isTyping) {
    // if we dont receive events after a certain about of time we will set to false
    setTypingIndicatorToFalseTimeOut = setTimeout(() => {
      dispatch(agentTypingIndicator.actions.setTypingIndicator({ ...event, isTyping: false }));
    }, TYPING_INDICATOR_SET_TO_FALSE_TIME_OUT);
  }
};

const onAgentReadReceiptEvent = (
  dispatch: AppDispatch,
  { agent, readAt }: WebChatAgentReadReceiptEvent
) => {
  dispatch(
    updateInitialState({
      agentReadReceipts: [{ agent, readAt }],
    } as WebChatInitialStateContent)
  );
};

interface NotificationProps {
  widgetConfiguration: WidgetConfiguration;
  subscriptionActive: boolean;
  participant?: WebChatParticipant;
}

export const useNotification = ({
  widgetConfiguration,
  participant,
  subscriptionActive,
}: NotificationProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    return subscribe(
      widgetConfiguration,
      participant,
      dispatch,
      subscriptionActive,
      (initialState: WebChatInitialState) => {
        const initialStateContent = initialState.content;
        dispatch(setInitialState(initialStateContent));
        dispatch(
          setEvents(
            initialStateContent.conversationEvents.filter(Boolean).map(event => {
              const eventWithTimestamp = { ...event, sentAt: initialState.timestamp };

              switch (event.type) {
                case NotificationEventsType.WEB_CHAT_AFTER_HOURS_MESSAGE:
                  return toAfterHoursEvent(eventWithTimestamp as WebChatAfterHoursMessageEvent);
                case NotificationEventsType.WEB_CHAT_INBOUND_MESSAGE:
                case NotificationEventsType.WEB_CHAT_OUTBOUND_MESSAGE:
                  return toMessageEvent(eventWithTimestamp as WebChatMessage);
                case NotificationEventsType.WEB_CHAT_SMART_RESPONSE_SELECT_QUESTION:
                  return toSmartResponseSelectQuestion(
                    eventWithTimestamp as WebChatSmartResponseSelectQuestion
                  );
              }
            })
          )
        );
      },
      (type: NotificationEventsType, event) => {
        const eventContent = { ...event.content, sentAt: event.timestamp };

        switch (type) {
          case NotificationEventsType.WEB_CHAT_INBOUND_MESSAGE: {
            const messageEvent = eventContent as WebChatInboundMessage;
            dispatch(
              mergeMessageEvent({
                ...toMessageEvent(messageEvent),
                correlationId: messageEvent.correlationId,
              })
            );
            break;
          }
          case NotificationEventsType.WEB_CHAT_OUTBOUND_MESSAGE:
            dispatch(addEvent(toMessageEvent(eventContent as WebChatOutboundMessage)));
            dbGet(AgentDB, 'key').then(agentId => {
              if (agentId !== (eventContent as WebChatOutboundMessage).message.key) {
                const agent = (eventContent as WebChatOutboundMessage).message.agent;
                agent && dispatch(fetchAgent(agent));
              }
            });
            break;
          case NotificationEventsType.WEB_CHAT_ENDED:
            dispatch(addEvent(toChatEndedEvent(eventContent as WebChatEnded)));
            Promise.all([
              dbPut(ConversationDB, true, 'conversationTerminated'),
              dbDelete(WidgetDB),
              dbDelete(AgentDB),
              dbDelete(ChannelDB),
              dbDelete(ParticipantDB, 'participantExternalKey'),
            ]).catch((error: Error) => {
              dispatch(setError({ type: ErrorType.DATA, message: error.message }));
            });
            break;
          case NotificationEventsType.WEB_CHAT_AFTER_HOURS_MESSAGE:
            dispatch(addEvent(toAfterHoursEvent(eventContent as WebChatAfterHoursMessageEvent)));
            break;

          case NotificationEventsType.AGENT_TYPING_INDICATOR:
            onAgentTypingIndicatorEvent(dispatch, eventContent);
            break;

          case NotificationEventsType.AGENT_READ_RECEIPT:
            onAgentReadReceiptEvent(dispatch, eventContent);
            break;
          case NotificationEventsType.WEB_CHAT_SURVEY_RATING_QUESTION:
            dispatch(
              addEvent(toWebChatSurveyInitializedEvent(eventContent as WebChatSurveyInitialized))
            );
            break;
          case NotificationEventsType.WEB_CHAT_SURVEY_FEEDBACK_QUESTION:
            dispatch(
              addEvent(
                toWebChatSurveyFeedbackQuestionEvent(eventContent as WebChatSurveyFeedbackQuestion)
              )
            );
            break;
          case NotificationEventsType.WEB_CHAT_SURVEY_RATING_RESPONSE:
            dispatch(
              addEvent(
                toWebChatSurveyRatingResponseEvent(eventContent as WebChatSurveyRatingResponse)
              )
            );
            break;
          case NotificationEventsType.WEB_CHAT_SURVEY_FEEDBACK_RESPONSE:
            dispatch(
              addEvent(
                toWebChatSurveyFeedbackResponseEvent(eventContent as WebChatSurveyFeedbackResponse)
              )
            );
            break;
          case NotificationEventsType.WEB_CHAT_SMART_RESPONSE_SELECT_QUESTION: {
            dispatch(
              addEvent(
                toWebChatSmartResponseSelectQuestionEvent(
                  eventContent as WebChatSmartResponseSelectQuestion
                )
              )
            );
            break;
          }
        }
      }
    );
  }, [
    widgetConfiguration?.appKey,
    widgetConfiguration?.hostReferer,
    participant,
    subscriptionActive,
  ]);

  return null;
};
